import {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FIELD_ARGS,
  INPUT_FORM_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { on } from '@ember/modifier';
import { or, reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import SpdsAutofocus from '../../../modifiers/spds-autofocus';
import Super from './base.gjs';
import styles from './input.module.scss';

const METADATA = Metadata.define({
  description: `Preconfigured HTML input element. Forms the basis for nearly all SPDS input components.

### Input types
Currently, the \`Spds::Core::Input\` component supports three input types:

* checkbox
* radio
* text

Both the \`checkbox\` and \`radio\` render invisibly to allow SPDS components to provide a custom mask.
To use a DS-compliant version of these input types, see the \`Spds::Input::Checkbox\` or \`Spds::Input::RadioButton\` documentation instead.`,
  extends: [
    pick(COMPONENT_STATE_ARGS, ['autofocus', 'disabled', 'error']),
    pick(ACCESSIBILITY_ARGS, ['ariaLabel']),
    pick(ACTION_ARGS, [
      'onBlur',
      'onChange',
      'onClick',
      'onFocus',
      'onInput',
      'onKeyDown',
      'onKeyUp',
    ]),
    pick(INPUT_FIELD_ARGS, [
      { name: 'type', required: true, initialValue: 'text' },
      'checked',
      'indeterminate',
      'inputClasses',
      'maxLength',
    ]),
    pick(INPUT_FORM_ARGS, [
      'name',
      'required',
      'value',
      { name: 'placeholder', initialValue: 'Placeholder' },
    ]),
  ],
  meta: {
    noBorder: Arg.boolean({ description: 'Hides the input border' }),
  },
  name: 'Input',
});

export default class SpdsCoreInput extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-input';
  classesToMap = ['error', 'inputClasses', 'noBorder'];
  defaultComponentName = 'Spds::Core::Input';
  styles = styles;

  @tracked baseElement;

  @or('args.groupName', 'args.name') name;
  @reads('args.value') value;

  get maxLength() {
    let { maxLength } = this.args;

    return maxLength > 0 ? maxLength : undefined;
  }

  get arguments() {
    const { additionalArguments = [] } = this.args;

    return [...this.constructor.metadata.validations, ...additionalArguments];
  }

  get componentName() {
    let { component = this.defaultComponentName } = this.args;

    return `${component} - instance #${this.id} - `;
  }

  onBlur = evt => {
    let { checked, id, name: groupName } = evt.target;
    let { value } = this;

    this.args.onBlur?.({ checked, event: evt, groupName, id, value });
  };

  onChange = evt => {
    let { checked, id, name: groupName } = evt.target;
    let { value } = this;

    this.args.onChange?.({ checked, event: evt, groupName, id, value });
  };

  onClick = evt => {
    let { checked, id, name: groupName } = evt.target;
    let { value } = this;

    this.args.onClick?.({ checked, event: evt, groupName, id, value });
  };

  onFocus = evt => {
    let { checked, id, name: groupName } = evt.target;
    let { value } = this;

    this.args.onFocus?.({ checked, event: evt, groupName, id, value });
  };

  onInput = evt => {
    this.args.onInput?.(evt);
  };

  onKeyDown = evt => {
    this.args.onKeyDown?.(evt);
  };

  onKeyUp = evt => {
    this.args.onKeyUp?.(evt);
  };

  <template>
    <input
      aria-label={{@ariaLabel}}
      checked={{@checked}}
      class={{this.classes}}
      data-test-spds-input={{this.id}}
      disabled={{@disabled}}
      id={{this.id}}
      indeterminate={{@indeterminate}}
      maxlength={{this.maxLength}}
      name={{this.name}}
      placeholder={{@placeholder}}
      required={{@required}}
      type={{@type}}
      value={{this.value}}
      {{on "blur" this.onBlur}}
      {{on "change" this.onChange}}
      {{on "click" this.onClick}}
      {{on "focus" this.onFocus}}
      {{on "input" this.onInput}}
      {{on "keydown" this.onKeyDown}}
      {{on "keyup" this.onKeyUp}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      {{SpdsAutofocus @autofocus}}
      ...attributes
    />
  </template>
}

export const CORE_INPUT_METADATA = SpdsCoreInput.metadata;
